import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { apiClient } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  Music, Calendar, Sparkles, Award, Trophy, Zap, Crown, CalendarHeart,
  Medal, Gem, Mic, Drum, Rocket,
  Lock, Trophy as TrophyIcon,
} from "lucide-react";

/**
 * Achievements page — a per-player view of unlocked + locked badges
 * computed from attendance data. Encourages engagement without leaderboards.
 *
 * Added in iter-66d.
 */

const ICON_MAP = {
  Music, Calendar, Sparkles, Award, Trophy, Zap, Crown, CalendarHeart,
  Medal, Gem, Mic, Drum, Rocket,
};

const TIER_STYLES = {
  bronze: {
    bg: "bg-amber-700/15 border-amber-700/30",
    iconColor: "text-amber-600",
    badgeClass: "border-amber-600/40 text-amber-500",
  },
  silver: {
    bg: "bg-slate-400/15 border-slate-400/30",
    iconColor: "text-slate-300",
    badgeClass: "border-slate-400/40 text-slate-300",
  },
  gold: {
    bg: "bg-primary/15 border-primary/30",
    iconColor: "text-primary",
    badgeClass: "border-primary/40 text-primary",
  },
  platinum: {
    bg: "bg-purple-500/15 border-purple-500/30",
    iconColor: "text-purple-300",
    badgeClass: "border-purple-400/40 text-purple-300",
  },
};

const BadgeCard = ({ badge }) => {
  const Icon = ICON_MAP[badge.icon] || TrophyIcon;
  const tier = TIER_STYLES[badge.tier] || TIER_STYLES.bronze;
  const pct = Math.min(100, Math.round((badge.progress / badge.target) * 100));

  return (
    <Card
      className={`glass-card transition-all ${badge.earned ? tier.bg : "opacity-60 grayscale"}`}
      data-testid={`achievement-${badge.id}`}
    >
      <CardContent className="p-5">
        <div className="flex items-start gap-4">
          <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${
            badge.earned ? tier.bg.replace("/15", "/25") : "bg-secondary/40"
          }`}>
            {badge.earned ? (
              <Icon className={`w-6 h-6 ${tier.iconColor}`} />
            ) : (
              <Lock className="w-5 h-5 text-muted-foreground" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <h3 className="font-heading font-semibold">{badge.name}</h3>
              <Badge variant="outline" className={`text-xs capitalize ${tier.badgeClass}`}>
                {badge.tier}
              </Badge>
            </div>
            <p className="text-sm text-muted-foreground mt-1">{badge.description}</p>
            <div className="mt-3 space-y-1">
              <div className="flex items-center justify-between text-xs">
                <span className="text-muted-foreground">
                  {badge.earned ? "Unlocked!" : `${badge.progress} / ${badge.target}`}
                </span>
                <span className="text-muted-foreground tabular-nums">{pct}%</span>
              </div>
              <div className="h-1.5 w-full rounded-full bg-secondary overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all ${badge.earned ? "bg-primary" : "bg-muted-foreground/40"}`}
                  style={{ width: `${pct}%` }}
                />
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const Achievements = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiClient.get("/me/achievements")
      .then((res) => setData(res.data))
      .catch(() => setData({ badges: [], earned_count: 0, total_count: 0, total_events_attended: 0 }))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  const earned = data?.badges?.filter((b) => b.earned) || [];
  const locked = data?.badges?.filter((b) => !b.earned) || [];

  return (
    <div className="space-y-8 animate-fade-in max-w-5xl">
      <div>
        <h1 className="text-3xl font-heading font-bold tracking-tight">Achievements</h1>
        <p className="text-muted-foreground mt-1">
          Earn badges by showing up. Your progress, in your hands.
        </p>
      </div>

      <Card className="glass-card border-primary/20">
        <CardContent className="p-6">
          <div className="grid sm:grid-cols-3 gap-4">
            <div className="space-y-1">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Badges earned</p>
              <p className="text-3xl font-heading font-bold text-primary" data-testid="badges-earned-count">
                {data?.earned_count || 0}
                <span className="text-base font-normal text-muted-foreground"> / {data?.total_count || 0}</span>
              </p>
            </div>
            <div className="space-y-1">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Events attended</p>
              <p className="text-3xl font-heading font-bold" data-testid="events-attended-count">
                {data?.total_events_attended || 0}
              </p>
            </div>
            <div className="space-y-1">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Next milestone</p>
              <p className="text-base mt-2">
                {locked.length > 0
                  ? <><span className="font-medium">{locked[0].name}</span> · {locked[0].target - locked[0].progress} to go</>
                  : "All badges earned! 🎉"}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {earned.length > 0 && (
        <section className="space-y-3">
          <h2 className="font-heading text-lg tracking-tight">Earned</h2>
          <div className="grid md:grid-cols-2 gap-4">
            {earned.map((b) => <BadgeCard key={b.id} badge={b} />)}
          </div>
        </section>
      )}

      {locked.length > 0 && (
        <section className="space-y-3">
          <h2 className="font-heading text-lg tracking-tight">In progress</h2>
          <div className="grid md:grid-cols-2 gap-4">
            {locked.map((b) => <BadgeCard key={b.id} badge={b} />)}
          </div>
        </section>
      )}

      <p className="text-xs text-muted-foreground">
        Achievements update automatically based on your event attendance.
        See something missing? <Link to="/events" className="text-primary hover:underline">Check upcoming events</Link>.
      </p>
    </div>
  );
};

export default Achievements;
